import { takeLatest, takeEvery, all } from 'redux-saga/effects'
import _ from 'lodash'

import { makeURL } from 'mednet-util/src/router'

import {
  makeFetchEffect,
  receiveAction,
  receiveReducer,
} from '../cns-util/reducer'

import {
  ADD_DIGEST_UPDATE,
  CREATE_DIGEST,
  DELETE_DIGEST_UPDATE,
  FETCH_TODAY_DIGEST,
  MERGE_TODAY_DIGEST_COHORTS,
  SPLIT_DIGEST_INTO_TWO_COHORTS,
} from './digest'
import { makeRequestName } from './request'

export const SEARCH_FOR_UPDATES = 'questionUpdate/SEARCH_FOR_UPDATES'
export const FETCH_UPDATE = 'questionUpdate/FETCH_UPDATE'
export const FETCH_UPDATE_IN_NOTIFICATION_USAGE =
  'questionUpdate/FETCH_UPDATE_IN_NOTIFICATION_USAGE'
export const FETCH_QUESTION_USAGE = 'questionUpdate/FETCH_QUESTION_USAGE'

export function searchQuestionUpdate(specialtyId, params, callback) {
  return {
    type: SEARCH_FOR_UPDATES,
    requestId: `${specialtyId}`,
    specialtyId,
    params,
    callback,
  }
}

export function fetchUpdate(updateId) {
  return {
    type: FETCH_UPDATE,
    requestId: `${updateId}`,
    updateId,
  }
}

export function fetchUpdateInNotificationUsage(
  specialtyId,
  updateIds,
  excludeNotificationIds
) {
  return {
    type: FETCH_UPDATE_IN_NOTIFICATION_USAGE,
    specialtyId,
    updateIds,
    excludeNotificationIds,
  }
}

export function fetchQuestionUsage(
  specialtyId,
  questionIds,
  excludeNotificationIds
) {
  return {
    type: FETCH_QUESTION_USAGE,
    specialtyId,
    questionIds,
    excludeNotificationIds,
  }
}

function* watchFetch() {
  yield makeFetchEffect(takeLatest, SEARCH_FOR_UPDATES, (action) =>
    makeURL(`questionUpdate/search/${action.specialtyId}`, action.params)
  )
  // takeEvery (not takeLatest): the search panel and the digest page both
  // dispatch this action, and takeLatest would cancel one in-flight fetch when
  // the other started, leaving some updates with no usage data and their "last
  // used" label blank. Usage data only accumulates in state, so concurrent
  // fetches merging is safe.
  yield makeFetchEffect(
    takeEvery,
    FETCH_UPDATE_IN_NOTIFICATION_USAGE,
    (action) =>
      makeURL(`questionUpdate/getUpdateInNotificationUsageJSON`, {
        updateIds: action.updateIds,
        excludeNotificationIds: action.excludeNotificationIds,
        specialtyId: action.specialtyId,
      })
  )
  // takeEvery for the same reason as the notification usage above: the search
  // panel and digest page can both request question usage, and cancelling one
  // would leave cards without their "another item" line.
  yield makeFetchEffect(takeEvery, FETCH_QUESTION_USAGE, (action) =>
    makeURL(`questionUpdate/getQuestionUsageJSON`, {
      questionIds: action.questionIds,
      specialtyId: action.specialtyId,
      excludeNotificationIds: action.excludeNotificationIds,
    })
  )
  yield makeFetchEffect(takeLatest, FETCH_UPDATE, (action) =>
    makeURL(`questionUpdate/getJSON/${action.updateId}`)
  )
}

export function* rootSaga() {
  yield all([watchFetch()])
}

const initialState = {
  search: {},
  notifications: {},
  updates: {},
  questionUsage: {},
}

const reduceUpdates = (updates, update) => {
  updates[update.questionUpdateId] = { ...update }
  return updates
}

export function reducer(state = initialState, action) {
  switch (action.type) {
    case receiveAction(ADD_DIGEST_UPDATE):
    case receiveAction(DELETE_DIGEST_UPDATE):
      if (action.response.digest?.updates) {
        return receiveReducer(state, action, () => ({
          updates: {
            ...state.updates,
            ...action.response.digest.updates.reduce(reduceUpdates, {}),
          },
        }))
      }
      return state
    case receiveAction(MERGE_TODAY_DIGEST_COHORTS):
    case receiveAction(SPLIT_DIGEST_INTO_TWO_COHORTS):
    case receiveAction(CREATE_DIGEST):
    case receiveAction(FETCH_TODAY_DIGEST):
      if (action.response.digests?.length) {
        return receiveReducer(state, action, () => ({
          updates: {
            ...state.updates,
            ..._.flatMap(
              action.response.digests,
              (digest) => digest.updates ?? []
            ).reduce(reduceUpdates, {}),
          },
        }))
      }
      return state
    case receiveAction(FETCH_UPDATE):
      if (action.response.update) {
        return receiveReducer(state, action, () => ({
          updates: {
            ...state.updates,
            ...{ [action.updateId]: action.response.update },
          },
        }))
      }
      return state
    case receiveAction(SEARCH_FOR_UPDATES): {
      return receiveReducer(state, action, () => ({
        search: {
          [action.specialtyId]: {
            ...state.search,
            answers: [...action.response.answers],
            questions: [...action.response.questions],
            polls: [...action.response.polls],
          },
        },
        updates: {
          ...state.updates,
          ...action.response.answers.reduce(reduceUpdates, {}),
          ...action.response.questions.reduce(reduceUpdates, {}),
          ...action.response.polls.reduce(reduceUpdates, {}),
        },
      }))
    }

    case receiveAction(FETCH_UPDATE_IN_NOTIFICATION_USAGE): {
      return receiveReducer(state, action, () => {
        const specialtyNotifications =
          state.notifications[action.specialtyId] || {}
        // The backend only returns updates that HAVE usage. Seed every requested
        // updateId with an empty record so an update with no usage reads as
        // "fetched, none" (defined but empty) rather than "not fetched yet"
        // (undefined). This matters under overlapping batches (takeEvery): the
        // page-shared request flag flips to loaded when the FIRST batch returns,
        // so a card still awaiting a later batch can't rely on that flag — it
        // reads its own record instead, and must not mistake absent for empty.
        // The real response is spread last so it overrides these defaults wherever
        // usage exists. (Mirrors FETCH_QUESTION_USAGE below.)
        const emptyNotificationDefaults = action.updateIds.reduce(
          (notificationsById, updateId) => {
            notificationsById[updateId] = { digests: [], newsletters: [] }
            return notificationsById
          },
          {}
        )
        return {
          notifications: {
            ...state.notifications,
            [action.specialtyId]: {
              ...specialtyNotifications,
              ...emptyNotificationDefaults,
              ...action.response.notifications,
            },
          },
        }
      })
    }

    case receiveAction(FETCH_QUESTION_USAGE): {
      return receiveReducer(state, action, () => {
        const specialtyUsage = state.questionUsage[action.specialtyId] || {}
        // The backend only returns questions that HAVE usage. Seed every requested
        // question with an empty record so a question with no usage reads as
        // "fetched, none" (defined but empty) rather than "not fetched yet"
        // (undefined). The real response is spread last so it overrides these
        // defaults wherever usage exists.
        const emptyUsageDefaults = action.questionIds.reduce(
          (usageById, questionId) => {
            usageById[questionId] = { digests: [], newsletters: [] }
            return usageById
          },
          {}
        )
        return {
          questionUsage: {
            ...state.questionUsage,
            [action.specialtyId]: {
              ...specialtyUsage,
              ...emptyUsageDefaults,
              ...action.response.questionUsage,
            },
          },
        }
      })
    }

    default:
      return state
  }
}

export function getUpdate(state, updateId) {
  return _.get(state, `questionUpdate.updates["${updateId}"]`)
}

export function getIsFetchUpdateLoading(state, updateId) {
  return _.get(
    state.request.requests,
    makeRequestName(FETCH_UPDATE, updateId),
    {}
  ).isLoading
}

export function getQuestionUsage(state, specialtyId, questionId) {
  return _.get(
    state,
    `questionUpdate.questionUsage["${specialtyId}"]["${questionId}"]`
  )
}

export function getQuestionUsageRequest(state) {
  return _.get(
    state.request.requests,
    makeRequestName(FETCH_QUESTION_USAGE),
    {}
  )
}
